import { memo } from 'react';
import PropTypes from 'prop-types';

/**
 * A labelled on/off setting (CT-5197 / CT-5198).
 *
 * The tickets call for "the DS toggle used by `createToggleListSection`", but
 * that switch lives inside `FilterSlideoutOptionRow` and neither it nor the
 * section renderer is exported from the `@aimsos/ui` barrel — the builder can
 * only reach it by rendering a whole `FilterSlideout`, which these settings are
 * not. This is that switch's markup and semantics (`role="switch"` +
 * `aria-checked`) rebuilt locally over theme tokens.
 *
 * Not a `Checkbox`: these are settings, not list selections, and the two read
 * differently to a screen reader.
 */
const SettingToggle = memo(function SettingToggle({ checked, label, description, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={[
        'flex w-full min-w-0 items-center justify-between gap-3 rounded-lg border border-edge px-3 py-2.5 text-left transition-colors',
        'hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
      ].join(' ')}
    >
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-medium text-content">{label}</span>
        {description ? (
          <span className="text-xs text-content-muted">{description}</span>
        ) : null}
      </span>

      <span
        aria-hidden
        className={[
          'relative h-5 w-9 shrink-0 rounded-full transition-colors',
          checked ? 'bg-brand-600' : 'bg-surface-muted border border-edge',
        ].join(' ')}
      >
        <span
          className={[
            'absolute top-0.5 h-4 w-4 rounded-full bg-surface shadow-sm transition-all',
            checked ? 'left-[18px]' : 'left-0.5',
          ].join(' ')}
        />
      </span>
    </button>
  );
});

SettingToggle.propTypes = {
  checked: PropTypes.bool,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default SettingToggle;
